/**
 * Modes screen — select a mode first, then confirm with PLAY.
 */

import {
    playUiModeCardHover,
    playUiModePanelPlay,
    playUiModeSelect,
    playUiModeSwitch,
} from "./ui-sounds";
import {
    isModesSelectPanelEnabled,
    runPlayTransition,
    shouldUsePlayTransition,
} from "./play-transition";

export type ModePlayAction = () => void;

type PanelDescCopy = { pc: string; mobile: string } | string;

type ModesSelectionHooks = {
    playActions: Record<string, ModePlayAction>;
};

const MODE_PANEL_TITLES: Record<string, string> = {
    normal: "Normal",
    progression: "Progression",
    wasd: "WASD",
    superdot: "Superdot",
    roulette: "Roulette",
    sandbox: "Sandbox",
    speedrun: "SpeedRun",
    penalty: "Golf",
    highway: "Highway",
    darkness: "Darkness",
    mirror: "Mirror",
    snake: "Snake",
    combo: "Combo",
    colorrush: "Color Rush",
    heat: "Heat",
    hunter: "Hunter",
    pop: "POP",
    counter: "Counter",
    aim: "AIM",
    hook: "Hook",
    tilt: "Tilt",
    multiplayer: "Multiplayer",
    local: "Local",
    orbit: "Orbit",
    delay: "Delay",
};

const CURSOR_MOVE_PC = "Move with your cursor.";
const CURSOR_MOVE_MOBILE = "Move with touch.";

const MODE_PANEL_DESCRIPTIONS: Record<string, PanelDescCopy> = {
    normal: {
        pc: `${CURSOR_MOVE_PC} Dodge bouncing balls that speed up over time. Survive as long as you can and rack up score.`,
        mobile: `${CURSOR_MOVE_MOBILE} Dodge bouncing balls that speed up over time. Survive as long as you can and rack up score.`,
    },
    progression: {
        pc: `${CURSOR_MOVE_PC} A shifting safe lane protects you — each level adds more balls and tighter pressure.`,
        mobile: `${CURSOR_MOVE_MOBILE} A shifting safe lane protects you — each level adds more balls and tighter pressure.`,
    },
    wasd: {
        pc: "Steer with the keyboard. Build momentum and never stall — balls punish hesitation.",
        mobile: "Steer with the on-screen D-pad. Build momentum and never stall — balls punish hesitation.",
    },
    superdot: {
        pc: `${CURSOR_MOVE_PC} Deploy shield, slow, barrier, and push powers on cooldown to outlast the swarm.`,
        mobile: `${CURSOR_MOVE_MOBILE} Deploy shield, slow, barrier, and push powers on cooldown to outlast the swarm.`,
    },
    roulette: "Rules rotate every few seconds — size, speed, and visibility change. Adapt instantly or die.",
    sandbox: "Tune every rule yourself. Experiment with ball counts, speeds, and modifiers in a private lab.",
    speedrun: {
        pc: `${CURSOR_MOVE_PC} Race through laser grids and wall mazes to the finish line as fast as possible.`,
        mobile: `${CURSOR_MOVE_MOBILE} Race through laser grids and wall mazes to the finish line as fast as possible.`,
    },
    penalty: {
        pc: `${CURSOR_MOVE_PC} Putt through laser fairways — fewest strokes to the hole wins.`,
        mobile: `${CURSOR_MOVE_MOBILE} Putt through laser fairways — fewest strokes to the hole wins.`,
    },
    highway: {
        pc: `${CURSOR_MOVE_PC} The road scrolls upward forever — stay inside the lane as gaps shrink.`,
        mobile: `${CURSOR_MOVE_MOBILE} The road scrolls upward forever — stay inside the lane as gaps shrink.`,
    },
    darkness: {
        pc: `${CURSOR_MOVE_PC} Your light shrinks over time — chase sparks before the dark closes in.`,
        mobile: `${CURSOR_MOVE_MOBILE} Your light shrinks over time — chase sparks before the dark closes in.`,
    },
    mirror: {
        pc: `${CURSOR_MOVE_PC} A mirror twin copies every move — keep both cursors clear of danger.`,
        mobile: `${CURSOR_MOVE_MOBILE} A mirror twin copies every move — keep both cursors clear of danger.`,
    },
    snake: {
        pc: `${CURSOR_MOVE_PC} Your trail stays behind you while red apples drift — never cross your path.`,
        mobile: `${CURSOR_MOVE_MOBILE} Your trail stays behind you while red apples drift — never cross your path.`,
    },
    combo: {
        pc: `${CURSOR_MOVE_PC} Graze balls to build a score multiplier — one clean miss resets the chain.`,
        mobile: `${CURSOR_MOVE_MOBILE} Graze balls to build a score multiplier — one clean miss resets the chain.`,
    },
    colorrush: {
        pc: `${CURSOR_MOVE_PC} Your color cycles on a timer — collect matching balls, dodge the rest.`,
        mobile: `${CURSOR_MOVE_MOBILE} Your color cycles on a timer — collect matching balls, dodge the rest.`,
    },
    heat: {
        pc: `${CURSOR_MOVE_PC} Stay too still and you freeze; move too wildly and you burn. Balance your temperature.`,
        mobile: `${CURSOR_MOVE_MOBILE} Stay too still and you freeze; move too wildly and you burn. Balance your temperature.`,
    },
    hunter: {
        pc: `${CURSOR_MOVE_PC} Dodge the swarm while red hunter balls spawn and chase you across the arena.`,
        mobile: `${CURSOR_MOVE_MOBILE} Dodge the swarm while red hunter balls spawn and chase you across the arena.`,
    },
    pop: {
        pc: `${CURSOR_MOVE_PC} Start with 50 large balls. Same-size hits split; large-or-bigger balls also split with each other. Minis fuse and grow without a size cap.`,
        mobile: `${CURSOR_MOVE_MOBILE} Start with 50 large balls. Same-size hits split; large-or-bigger balls also split with each other. Minis fuse and grow without a size cap.`,
    },
    counter: {
        pc: `${CURSOR_MOVE_PC} One hundred numbered balls — touch them in order before the five-second timer expires.`,
        mobile: `${CURSOR_MOVE_MOBILE} One hundred numbered balls — touch them in order before the five-second timer expires.`,
    },
    aim: {
        pc: `${CURSOR_MOVE_PC} Thirty balls on screen — click the golden one within two seconds per wave.`,
        mobile: `${CURSOR_MOVE_MOBILE} Thirty balls on screen — tap the golden one within two seconds per wave.`,
    },
    hook: {
        pc: `${CURSOR_MOVE_PC} Aim a laser, launch a hook along it, and reel in normal balls — reds kill on return.`,
        mobile: `${CURSOR_MOVE_MOBILE} Aim with the joystick, tap to launch the hook, and reel in normals — reds kill on return.`,
    },
    tilt: {
        pc: "Tilt your phone or tablet to steer — the cursor rolls like a ball on a tilted table. Requires a mobile/tablet with motion sensors.",
        mobile: "Tilt the device to steer — the cursor rolls like a ball on a tilted table. No finger control; lean left, right, forward, or back.",
    },
    orbit: {
        pc: "Orbit the core with A/D or ←/→, or slide left-right. Intercept inbound balls before they breach the center.",
        mobile: "Hold the left/right edges or slide left-right. Intercept inbound balls before they breach the center.",
    },
    delay: {
        pc: `${CURSOR_MOVE_PC} Steer a faint ghost instantly — your real cursor (with cosmetics) replays that path 1 second later. Only the delayed cursor dies on contact.`,
        mobile: `${CURSOR_MOVE_MOBILE} Steer a faint ghost instantly — your real cursor (with cosmetics) replays that path 1 second later. Only the delayed cursor dies on contact.`,
    },
    multiplayer: "Create or join a room online. Pick a mode and outlast friends — or survive together in co-op.",
    local: "Two to four players on one device. Split the screen into zones and compete or cooperate locally.",
};

let hooks: ModesSelectionHooks | null = null;
let selectedModeId: string | null = null;
let mounted = false;
let contentSwapTimer: ReturnType<typeof setTimeout> | null = null;

function isModesTouchUi(): boolean {
    if (typeof matchMedia === "undefined") return false;
    if (matchMedia("(pointer: coarse)").matches) return true;
    if (matchMedia("(max-width: 820px)").matches && navigator.maxTouchPoints > 0) return true;
    return false;
}

function resolvePanelDesc(desc: PanelDescCopy | undefined): string {
    if (!desc) return "";
    if (typeof desc === "string") return desc;
    return isModesTouchUi() ? desc.mobile : desc.pc;
}

const modeScreen = () => document.getElementById("modeScreen");
const panel = () => document.getElementById("modesSelectPanel");
const panelTitle = () => document.getElementById("modesSelectPanelTitle");
const panelDesc = () => document.getElementById("modesSelectPanelDesc");

export function getSelectedModeId(): string | null {
    return selectedModeId;
}

export function syncModeSelectionHighlight(): void {
    document.querySelectorAll<HTMLElement>("#modesContainer .modeBox[data-mode-id]").forEach((box) => {
        const id = box.getAttribute("data-mode-id");
        box.classList.toggle("modeBox--selected", !!id && id === selectedModeId);
        box.setAttribute("aria-pressed", id === selectedModeId ? "true" : "false");
    });
}

function clearContentSwapTimer(): void {
    if (contentSwapTimer) {
        clearTimeout(contentSwapTimer);
        contentSwapTimer = null;
    }
}

function retriggerPanelMotion(): void {
    const el = panel();
    if (!el) return;
    el.classList.remove("modesSelectPanel--animate");
    void el.offsetWidth;
    el.classList.add("modesSelectPanel--animate");
}

function fitModesSelectPanelTitle(): void {
    const titleEl = panelTitle();
    const wrap = titleEl?.closest<HTMLElement>(".modesSelectPanelTitleWrap");
    if (!titleEl || !wrap) return;

    const styles = getComputedStyle(titleEl);
    const maxPx = parseFloat(styles.getPropertyValue("--modes-panel-title-max")) || 38;
    const minPx = parseFloat(styles.getPropertyValue("--modes-panel-title-min")) || 12;
    const maxW = wrap.clientWidth;
    if (maxW <= 0) {
        requestAnimationFrame(() => fitModesSelectPanelTitle());
        return;
    }

    // Measure unconstrained: overflow:hidden + max-width:100% collapses scrollWidth
    // to the box width, so clipping is invisible to the old shrink loop.
    titleEl.style.setProperty("--modes-panel-title-size", `${maxPx}px`);
    titleEl.style.maxWidth = "none";
    titleEl.style.width = "max-content";
    titleEl.style.overflow = "visible";

    let lo = minPx;
    let hi = maxPx;
    let best = minPx;
    for (let i = 0; i < 18; i++) {
        const mid = (lo + hi) / 2;
        titleEl.style.setProperty("--modes-panel-title-size", `${mid}px`);
        if (titleEl.scrollWidth <= maxW + 0.5) {
            best = mid;
            lo = mid;
        } else {
            hi = mid;
        }
    }

    titleEl.style.setProperty("--modes-panel-title-size", `${Math.floor(best * 10) / 10}px`);
    titleEl.style.maxWidth = "";
    titleEl.style.width = "";
    titleEl.style.overflow = "";
}

function applyPanelContent(modeId: string): void {
    const title = MODE_PANEL_TITLES[modeId] ?? modeId;
    const desc = resolvePanelDesc(MODE_PANEL_DESCRIPTIONS[modeId]);
    const titleEl = panelTitle();
    const descEl = panelDesc();
    if (titleEl && titleEl.textContent !== title) titleEl.textContent = title;
    if (descEl && descEl.textContent !== desc) descEl.textContent = desc;
    fitModesSelectPanelTitle();
    requestAnimationFrame(() => fitModesSelectPanelTitle());
}

function showPanel(modeId: string, contentSwap: boolean): void {
    const screen = modeScreen();
    const el = panel();
    if (!screen || !el) return;

    const wasVisible = el.classList.contains("modesSelectPanel--visible");

    screen.classList.add("modesScreen--hasSelection");
    el.classList.remove("hidden");
    el.classList.add("modesSelectPanel--visible");

    if (contentSwap && wasVisible) {
        el.classList.add("modesSelectPanel--contentSwap");
        clearContentSwapTimer();
        contentSwapTimer = setTimeout(() => {
            contentSwapTimer = null;
            applyPanelContent(modeId);
            el.classList.remove("modesSelectPanel--contentSwap");
            retriggerPanelMotion();
        }, 180);
        return;
    }

    applyPanelContent(modeId);
    if (!wasVisible) retriggerPanelMotion();
}

function hidePanel(): void {
    clearContentSwapTimer();
    const screen = modeScreen();
    const el = panel();
    screen?.classList.remove("modesScreen--hasSelection");
    el?.classList.remove("modesSelectPanel--visible", "modesSelectPanel--animate", "modesSelectPanel--contentSwap");
    el?.classList.add("hidden");
}

function runModeAction(modeId: string, origin?: { x: number; y: number } | null): void {
    const action = hooks?.playActions[modeId];
    if (!action) return;
    if (shouldUsePlayTransition(modeId)) runPlayTransition(action, { origin });
    else action();
}

export function activateModeFromCard(modeId: string, origin?: { x: number; y: number } | null): void {
    if (!hooks?.playActions[modeId]) return;
    if (isModesSelectPanelEnabled()) {
        selectModeOnScreen(modeId);
        return;
    }
    playUiModeSelect();
    runModeAction(modeId, origin);
}

/** Start a mode immediately (skips Modes select panel). Used by Home Continue / Suggested. */
export function startModeById(modeId: string, origin?: { x: number; y: number } | null): void {
    if (!hooks?.playActions[modeId]) return;
    playUiModeSelect();
    runModeAction(modeId, origin);
}

export function selectModeOnScreen(modeId: string): void {
    if (!hooks?.playActions[modeId]) return;

    if (selectedModeId === modeId) {
        playSelectedMode();
        return;
    }

    const swapping = selectedModeId != null;
    if (swapping) playUiModeSwitch();
    else playUiModeSelect();

    selectedModeId = modeId;
    syncModeSelectionHighlight();
    showPanel(modeId, swapping);
}

export function clearModeSelection(): void {
    clearContentSwapTimer();
    selectedModeId = null;
    syncModeSelectionHighlight();
    hidePanel();
}

export function playSelectedMode(origin?: { x: number; y: number } | null): void {
    if (!selectedModeId || !hooks) return;
    playUiModePanelPlay();
    runModeAction(selectedModeId, origin);
}

export function mountModesSelectionPanel(h: ModesSelectionHooks): void {
    if (mounted) return;
    mounted = true;
    hooks = h;

    const playCircle = document.getElementById("modesSelectPlayCircle");
    playCircle?.addEventListener("click", (e) => {
        if (e.button !== 0) return;
        playSelectedMode({ x: e.clientX, y: e.clientY });
    });
    playCircle?.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        const rect = playCircle.getBoundingClientRect();
        playSelectedMode({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    });

    let resizeRaf = 0;
    window.addEventListener(
        "resize",
        () => {
            if (!selectedModeId || resizeRaf) return;
            resizeRaf = requestAnimationFrame(() => {
                resizeRaf = 0;
                if (selectedModeId) applyPanelContent(selectedModeId);
            });
        },
        { passive: true },
    );
}

export function wireModesContainerSelection(): void {
    const container = document.getElementById("modesContainer");
    if (!container || container.dataset.selectionWired === "1") return;
    container.dataset.selectionWired = "1";

    const onCardActivate = (box: HTMLElement, origin?: { x: number; y: number } | null) => {
        const modeId = box.getAttribute("data-mode-id");
        if (!modeId) return;
        activateModeFromCard(modeId, origin);
    };

    container.addEventListener("click", (e) => {
        if (e.button !== 0) return;
        if (e.target instanceof Element && e.target.closest(".modeBoxMenuBtn")) return;
        const box = (e.target as HTMLElement).closest<HTMLElement>(".modeBox[data-mode-id]");
        if (!box || box.classList.contains("hidden")) return;
        onCardActivate(box, { x: e.clientX, y: e.clientY });
    });

    container.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        const box = (e.target as HTMLElement).closest<HTMLElement>(".modeBox[data-mode-id]");
        if (!box || box.classList.contains("hidden")) return;
        e.preventDefault();
        const rect = box.getBoundingClientRect();
        onCardActivate(box, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    });

    let lastHoverCard: HTMLElement | null = null;
    container.addEventListener(
        "pointerenter",
        (e) => {
            if (!(e.target instanceof Element) || e.pointerType === "touch") return;
            const box = e.target.closest<HTMLElement>(".modeBox[data-mode-id]");
            if (!box || box.classList.contains("hidden") || box === lastHoverCard) return;
            lastHoverCard = box;
            playUiModeCardHover();
        },
        true,
    );
    container.addEventListener(
        "pointerleave",
        (e) => {
            if (!(e.target instanceof Element)) return;
            const box = e.target.closest<HTMLElement>(".modeBox[data-mode-id]");
            if (box && box === lastHoverCard) lastHoverCard = null;
        },
        true,
    );
}
